import { RepeatOutlined as RepeatIcon } from "@mui/icons-material";
import { Box, Chip, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { AdminEmptyState } from "@/components/common/AdminEmptyState";
import { LongRunningDataLoader } from "@/components/common/LongRunningDataLoader";
import type { ContinuousReadOccurrence } from "@/hooks/useContinuousReadsReport";
import OccurrencesTableBase, {
  buildOccurrenceStoreLabel,
  defaultOccurrenceCsvTime,
  defaultOccurrenceTimeCell,
  type OccurrenceColumn,
} from "@/pages/reports/shared/OccurrencesTableBase";
import type { Store } from "@/services/features/StoreService";

interface ContinuousReadsOccurrencesTableProps {
  data: ContinuousReadOccurrence[];
  loading: boolean;
  error: string | null;
  storeTimezones: Record<number, string>;
  stores: Store[];
  loadingKey: string;
}

const ContinuousReadsOccurrencesTable: React.FC<ContinuousReadsOccurrencesTableProps> = ({
  data,
  loading,
  error,
  storeTimezones,
  stores,
  loadingKey,
}) => {
  const { t, i18n } = useTranslation();

  const columns = React.useMemo<OccurrenceColumn<ContinuousReadOccurrence>[]>(
    () => [
      {
        id: "time",
        label: t("storeInsights.continuousReadsReport.fields.time"),
        sortKey: "createdAt",
        renderCell: defaultOccurrenceTimeCell,
        getCsvValue: defaultOccurrenceCsvTime,
        headerSx: { minWidth: 130 },
      },
      {
        id: "image",
        label: t("storeInsights.continuousReadsReport.fields.image"),
        renderCell: (_row, context) => context.renderImageCell(_row),
        headerSx: { width: 80 },
        cellSx: { width: 80 },
      },
      {
        id: "product",
        label: t("storeInsights.continuousReadsReport.fields.product"),
        sortKey: "productName",
        renderCell: (row) => (
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#001F3F" }}>
            {row.productName ||
              t("storeInsights.continuousReadsReport.placeholders.unknownProduct")}
          </Typography>
        ),
        getCsvValue: (row) =>
          row.productName || t("storeInsights.continuousReadsReport.placeholders.unknownProduct"),
        headerSx: { minWidth: 220 },
      },
      {
        id: "sku",
        label: t("storeInsights.continuousReadsReport.fields.sku"),
        sortKey: "sku",
        renderCell: (row) => (
          <Typography sx={{ fontSize: 13, color: "#001F3F", whiteSpace: "nowrap" }}>
            {row.sku}
          </Typography>
        ),
        getCsvValue: (row) => row.sku,
      },
      {
        id: "category",
        label: t("storeInsights.continuousReadsReport.fields.category"),
        sortKey: "category",
        renderCell: (row) =>
          row.category && row.category !== "--" ? (
            <Chip
              label={row.category}
              size="small"
              sx={{ bgcolor: "#F1F5F9", color: "#64748B", fontSize: 12, height: 24 }}
            />
          ) : (
            <Typography sx={{ fontSize: 13, color: "#64748B" }}>{row.category}</Typography>
          ),
        getCsvValue: (row) => row.category,
      },
      {
        id: "epc",
        label: t("storeInsights.continuousReadsReport.fields.epc"),
        sortKey: "epc",
        renderCell: (row) => (
          <Typography
            sx={{
              fontFamily: "monospace",
              fontSize: 12,
              color: "#64748B",
              maxWidth: 170,
              overflowWrap: "anywhere",
            }}
          >
            {row.epc}
          </Typography>
        ),
        getCsvValue: (row) => row.epc,
        headerSx: { minWidth: 170 },
      },
      {
        id: "room",
        label: t("storeInsights.continuousReadsReport.fields.room"),
        sortKey: "roomLabel",
        renderCell: (row) => (
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#001F3F", lineHeight: 1.25 }}>
              {row.roomLabel}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#64748B", lineHeight: 1.25 }}>
              {row.roomNumber}
            </Typography>
          </Box>
        ),
        getCsvValue: (row) => row.roomLabel,
      },
      {
        id: "store",
        label: t("storeInsights.continuousReadsReport.fields.store"),
        sortKey: "storeName",
        renderCell: (row) => (
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#001F3F", lineHeight: 1.25 }}>
              {row.storeName}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#64748B", lineHeight: 1.25 }}>
              {row.storeNumber}
            </Typography>
          </Box>
        ),
        getCsvValue: (row) => buildOccurrenceStoreLabel(row.storeName, row.storeNumber),
      },
    ],
    [t],
  );

  return (
    <OccurrencesTableBase
      title={t("storeInsights.continuousReadsReport.sections.occurrences")}
      data={data}
      loading={loading}
      error={error}
      storeTimezones={storeTimezones}
      columns={columns}
      emptyMessage={t("storeInsights.continuousReadsReport.empty.noReads")}
      emptyState={
        <AdminEmptyState
          icon={<RepeatIcon sx={{ fontSize: 40 }} />}
          text={t("storeInsights.continuousReadsReport.empty.noReads")}
        />
      }
      loadingCallout={
        <LongRunningDataLoader
          variant="table"
          isLoading={true}
          baseLabels={[
            t("storeInsights.continuousReadsReport.loading.reads"),
            t("storeInsights.common.loading.readingActivity"),
            t("storeInsights.common.loading.groupingResults"),
            t("storeInsights.common.loading.calculatingInsights"),
            t("storeInsights.common.loading.preparingResults"),
          ]}
          loopingLabels={[
            t("storeInsights.common.loading.stillWorking"),
            t("storeInsights.common.loading.thanksForPatience"),
          ]}
          estimate={{
            meanTimePerStoreMs: 7_000,
            storeCount: stores.length,
            growth: "logarithmic",
            logarithmicLinearWeight: 0.18,
          }}
          locale={i18n.resolvedLanguage || i18n.language}
          loadingKey={loadingKey}
          estimateLabel={(duration) => t("storeComparison.loading.usuallyUnder", { duration })}
        />
      }
      exportFilePrefix="continuous_reads_export"
      stores={stores}
      paginate
    />
  );
};

export default ContinuousReadsOccurrencesTable;
